import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Conflict = components['schemas']['CannibalizationConflictOut']
type ConflictPage = components['schemas']['ConflictPageOut']

type Scope = 'all' | 'within'

/**
 * Cannibalization — S-D5's `seo#cannib`, on real conflicts (S-B24).
 *
 * The design's own note is the whole argument for this shape: cannibalization is
 * **invisible to most site owners** — two of your own pages fighting over one
 * query — so a card shows the pair, the query, and *one* recommended action,
 * because the fix is a judgement call that must not be made silently.
 *
 * **Nothing here is applied.** *Merge & redirect* and *Apply the split* are
 * changes to somebody's website, which is the approval queue and the publishers
 * in later releases. What this screen records is the tenant's judgement, so the
 * next run stops raising the pair as new and the card stops asking. Saying that
 * out loud in the footer is the difference between an honest screen and one that
 * implies work AugRank has not done.
 *
 * **The cross-site case gets different buttons and it is not cosmetic.** S-D5:
 * *"you cannot 301 between two domains you sell different things from"*. The
 * server never sends `merge_redirect` for a cross-site conflict, and this screen
 * never offers it — two independent statements of one rule, which is what makes
 * it survive an edit to either.
 */
export function Cannibalization(): JSX.Element {
  const toast = useToast()
  const [scope, setScope] = useState<Scope>('all')

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const firstSite = listed[0]?.id ?? null

  const conflicts = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/cannibalization', {
          params:
            scope === 'within' && firstSite !== null
              ? { query: { site_id: firstSite } }
              : { query: {} },
          signal,
        }),
      ),
    [scope, firstSite],
  )

  const decide = useMutation((input: { group: string; status: string }) =>
    call(() =>
      api.POST('/api/v1/seo/cannibalization/{conflict_group}', {
        params: { path: { conflict_group: input.group } },
        body: { status: input.status },
      }),
    ),
  )

  const found = conflicts.state.data?.conflicts ?? []
  const crossSite = found.filter((row) => row.cross_site).length

  function record(group: string, status: string, done: string): void {
    void decide.run({ group, status }).then((result) => {
      if (!result.ok) return
      conflicts.refetch()
      toast.show({ kind: 'success', title: done })
    })
  }

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Keyword cannibalization</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <div className="app-seg" role="group" aria-label="Which pages">
            <button
              type="button"
              aria-pressed={scope === 'within'}
              onClick={() => {
                setScope('within')
              }}
            >
              Within a site
            </button>
            <button
              type="button"
              aria-pressed={scope === 'all'}
              onClick={() => {
                setScope('all')
              }}
            >
              Across your sites
            </button>
          </div>
        ) : null}
        <Link className="ar-link" to="/seo/keywords">
          Back to keywords
        </Link>
      </div>

      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        Two of your pages competing for one search. Google picks between them, and neither ranks as
        well as one strong page would. AugRank finds these by comparing what your pages actually say
        — nothing is changed until you decide.
      </p>

      {decide.state.status === 'error' ? <InlineError failure={decide.state.failure} /> : null}

      {conflicts.state.status === 'loading' ? (
        <Loading label="Loading conflicts" />
      ) : conflicts.state.status === 'error' ? (
        <ErrorState failure={conflicts.state.failure} onRetry={conflicts.refetch} />
      ) : found.length === 0 ? (
        <Empty
          title="No pages are competing with each other"
          body="Either nothing is, or AugRank has not read your rankings yet. Refreshing from the keywords screen checks both."
          action={<Link to="/seo/keywords">Go to keywords</Link>}
        />
      ) : (
        <div className="ar-stack">
          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              ⚠
            </span>
            <div className="ar-alert__body">
              <strong>{summarise(found.length, crossSite)}</strong>
              Google is picking between them and neither ranks as well as one strong page would.
            </div>
          </div>
          {found.map((conflict) => (
            <ConflictCard
              key={conflict.conflict_group}
              conflict={conflict}
              pending={decide.pending}
              onRecord={record}
            />
          ))}
        </div>
      )}
    </>
  )
}

function summarise(total: number, crossSite: number): string {
  const within = total - crossSite
  const parts: string[] = []
  if (within > 0) parts.push(`${String(within)} pair${within === 1 ? '' : 's'} within a site`)
  if (crossSite > 0) {
    parts.push(`${String(crossSite)} pair${crossSite === 1 ? '' : 's'} across your sites`)
  }
  return parts.join(', ')
}

/**
 * One conflict: the query, the pages, what AugRank would do, and the decision.
 *
 * The similarity is shown as a badge because it is the evidence — *"92% similar"*
 * is what makes the pair believable when a tenant looks at two URLs and thinks
 * they are different pages. The impact is shown in impressions rather than as a
 * predicted position gain, because nothing in this product can honestly produce
 * the second number.
 */
function ConflictCard({
  conflict,
  pending,
  onRecord,
}: {
  conflict: Conflict
  pending: boolean
  onRecord: (group: string, status: string, done: string) => void
}): JSX.Element {
  const decided = conflict.status !== 'open'
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>&ldquo;{conflict.query}&rdquo;</h3>
        <span className="ar-card__spacer" />
        {conflict.cross_site ? (
          <span className="ar-badge ar-badge--warning">Across your sites</span>
        ) : null}
        {decided ? <span className="ar-badge">{statusLabel(conflict.status)}</span> : null}
        <span className="ar-badge ar-badge--danger">
          {(conflict.similarity * 100).toFixed(0)}% similar
        </span>
      </div>
      <div className="ar-card__body ar-stack">
        <div className="ar-grid ar-grid--2">
          {conflict.pages.map((page) => (
            <PagePanel key={page.id} page={page} crossSite={conflict.cross_site} />
          ))}
        </div>
        <div className="ar-panel">
          <strong>What we&rsquo;d do</strong>
          <p className="ar-text-body-sm ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
            {advice(conflict)}
          </p>
          <p
            className="ar-muted"
            style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
          >
            About {Math.round(conflict.impact).toLocaleString('en-IN')} of the impressions for this
            search are going to a page that is not the one you would want ranking. Nothing happens
            until you approve.
          </p>
        </div>
      </div>
      <div className="ar-card__footer">
        <Button
          disabled={pending || decided}
          onClick={() => {
            onRecord(
              conflict.conflict_group,
              'acknowledged',
              'Noted — this pair will not be raised as new again.',
            )
          }}
        >
          {conflict.recommendation === 'merge_redirect'
            ? 'I\u2019ll merge these'
            : conflict.recommendation === 'split_intent'
              ? 'I\u2019ll split the intent'
              : 'I\u2019ll differentiate these'}
        </Button>
        <span className="ar-card__spacer" />
        <Button
          tone="ghost"
          disabled={pending}
          onClick={() => {
            onRecord(
              conflict.conflict_group,
              decided ? 'open' : 'dismissed',
              decided ? 'Back on the list.' : 'Ignored.',
            )
          }}
        >
          {decided ? 'Put it back' : 'Ignore this pair'}
        </Button>
      </div>
    </section>
  )
}

/** One page of the pair, with the figures that decide which should win. */
function PagePanel({ page, crossSite }: { page: ConflictPage; crossSite: boolean }): JSX.Element {
  return (
    <div className="ar-panel">
      <div className="ar-mono ar-text-body-sm">
        {crossSite ? `${page.domain}${pathOf(page.url)}` : pathOf(page.url)}
      </div>
      <div className="ar-row" style={{ marginTop: 'var(--ar-space-3)', gap: 'var(--ar-space-5)' }}>
        <Figure
          label="Position"
          value={
            page.position === null || page.position === undefined ? '—' : page.position.toFixed(0)
          }
        />
        <Figure label="Clicks" value={page.clicks.toLocaleString('en-IN')} />
        <Figure label="Impressions" value={page.impressions.toLocaleString('en-IN')} />
      </div>
      {page.primary ? (
        <p
          className="ar-muted"
          style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
        >
          The stronger of the two on the evidence.
        </p>
      ) : null}
      {!page.read ? (
        <p
          className="ar-muted"
          style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
        >
          AugRank could not read this page, so the comparison used the other one only.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The recommendation, in the tenant's terms rather than as a slug.
 *
 * Composed here rather than sent by the server because it is copy: the server
 * sends *which* of the three this is, and the sentence around it belongs with
 * the screen that renders it — which is the same split the audit screen makes
 * between a finding's `code` and its label.
 */
function advice(conflict: Conflict): string {
  const strongest = conflict.pages.find((page) => page.primary)
  const weakest = conflict.pages.find((page) => !page.primary)
  const strong = strongest === undefined ? 'the stronger page' : pathOf(strongest.url)
  const weak = weakest === undefined ? 'the other page' : pathOf(weakest.url)

  if (conflict.recommendation === 'merge_redirect') {
    return `Merge ${weak} into ${strong} — it has more clicks and a better position — then redirect the old address to it, so the strength of both lands on one page.`
  }
  if (conflict.recommendation === 'split_intent') {
    return `These are two of your own sites, so a redirect would throw away one of the brands. Let ${strong} own this search, and retarget ${weak} at a related term nobody in your set holds.`
  }
  return `Neither page is clearly stronger — one wins on clicks and the other on position — so this is your call. Either make ${weak} about something distinctly different, or fold it into ${strong}.`
}

function statusLabel(status: string): string {
  if (status === 'acknowledged') return 'Acknowledged'
  if (status === 'dismissed') return 'Ignored'
  if (status === 'resolved') return 'Resolved'
  return 'Open'
}

function Figure({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
        {label}
      </div>
      <strong className="ar-num">{value}</strong>
    </div>
  )
}

/** A URL as its path — the domain is on the card when it matters. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}
